import type { CallMetricsResponse } from "src/components/Dashboard/CallLogs/types";
import { capitalize } from "lodash";
import { Eye, Smile, Speech, Star } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/utils";
import { Button } from "../../../components/ui/Button";
import { useFetch } from "../../../utils/fetch";
import GradientLoadingAnimation from "../../Reusables/GradientLoadingAnimation";
import PieChart from "../Analytics/components/PieChart";
import { IssuesList } from "./MetricsIssueList";
import MetricsPlaceholder from "./MetricsPlaceholder";
import { MetricValue } from "./MetricValue";
import { PieChartWithNeedle } from "./PieChartWithNeedle";

interface MetricsProps {
  callId: string;
}

export default function Metrics({ callId }: MetricsProps) {
  const { data, isLoading } = useFetch(`/v1/calls/metrics?call_id=${callId}`, {
    queryKey: ["recordingMetrics", callId],
  });
  const metrics = data as CallMetricsResponse | undefined;

  const pieChartData = Object.entries(
    metrics?.utterance_summary?.utteranceTypes || {},
  ).map(([key, value]) => ({
    utterance_type: capitalize(key),
    count: value,
  }));

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center pt-10">
        <GradientLoadingAnimation
          message="Loading call metrics..."
          variant="roseGarden"
          rounded="full"
        />
      </div>
    );
  }

  if (!metrics) {
    return <MetricsPlaceholder />;
  }

  const backgroundNoiseCount = metrics?.call_analysis?.issues?.filter(
    (issue) => issue.type === "background_noise",
  ).length;

  const repetitionCount = metrics?.call_analysis?.issues?.filter(
    (issue) => issue.type === "agent_repetition",
  ).length;

  const userInterruptionCount =
    metrics?.call_analysis?.user_interruption_count || 0;
  const agentInterruptionCount =
    metrics?.call_analysis?.agent_interruption_count || 0;

  const speakerChanges = Math.max(
    0,
    (metrics?.utterance_summary?.uniqueSpeakers.length || 2) - 2,
  );

  const sentimentScore = metrics.call_analysis?.sentiment_score || 0;

  const { sentimentText, sentimentBadge } = getSentimentText(sentimentScore);

  return (
    <div className="flex flex-col gap-5 p-4">
      <div className="flex flex-col gap-3 pb-5">
        <span className="text-lg font-medium">Metrics Overview</span>
        <div
          className={`
            grid grid-cols-1 gap-x-2 gap-y-2
            md:grid-cols-2
          `}
        >
          <div
            className={`
              bg-card text-card-foreground flex flex-col gap-2 overflow-hidden
              rounded-md border border-gray-300 p-4 shadow-xs
            `}
          >
            <span className="flex items-center gap-2 text-sm text-gray-700">
              <div
                className={`
                  flex items-center justify-center gap-1 rounded-md bg-gray-200
                  p-1.5
                `}
              >
                <Star className="h-4 w-4" />
              </div>
              Agent Quality
            </span>
            <div
              className={`
                flex h-full flex-col items-center justify-center gap-2
              `}
            >
              <span
                className={`
                  -mt-2 flex items-center justify-center text-lg font-medium
                `}
              >
                <PieChartWithNeedle
                  value={(metrics.final_call_score || 0) * 100}
                />
              </span>
            </div>
          </div>
          <div
            className={`
              bg-card text-card-foreground overflow-x-auto rounded-md border
              border-gray-300 p-4 shadow-xs
            `}
          >
            <div className="flex flex-col gap-2">
              <span className="flex items-center gap-2 text-sm text-gray-700">
                <div
                  className={`
                    flex items-center justify-center gap-1 rounded-md
                    bg-gray-200 p-1.5
                  `}
                >
                  <Speech className="h-4 w-4" />
                </div>
                Word Count
              </span>
              <div className="-mt-2 h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={150}
                    height={40}
                    margin={{
                      top: 20,
                      left: -30,
                    }}
                    data={[
                      {
                        xName: "User",
                        name: "User word count",
                        "Word count": metrics.utterance_summary?.userWords,
                      },
                      {
                        xName: "Agent",
                        name: "Agent word count",
                        "Word count": metrics.utterance_summary?.agentWords,
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="xName" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="Word count" fill="#000" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <div
            className={`
              bg-card text-card-foreground overflow-x-auto rounded-md border
              border-gray-300 p-4 shadow-xs
            `}
          >
            <div className="flex flex-col gap-2">
              <span className="flex items-center gap-2 text-sm text-gray-700">
                <div
                  className={`
                    flex items-center justify-center gap-1 rounded-md
                    bg-gray-200 p-1.5
                  `}
                >
                  <Eye className="h-4 w-4" />
                </div>
                Issues detected
              </span>
              <div className="flex items-center gap-4 pt-2 pl-0.5">
                <span className="text-lg">
                  {metrics.call_analysis?.issues.length || 0}
                </span>
                {(metrics.call_analysis?.issues.length || 0) > 0 && (
                  <div className="flex items-center text-gray-500">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-500"
                      onClick={() => {
                        // Scroll to the issues section
                        const issuesSection =
                          document.querySelector("#issues-section");
                        if (issuesSection) {
                          issuesSection.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                    >
                      View Issues
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            className={`
              bg-card text-card-foreground overflow-x-auto rounded-md border
              border-gray-300 p-4 shadow-xs
            `}
          >
            <div className="flex flex-col gap-2">
              <span className="flex items-center gap-2 text-sm text-gray-700">
                <div
                  className={`
                    flex items-center justify-center gap-1 rounded-md
                    bg-gray-200 p-1.5
                  `}
                >
                  <Smile className="h-4 w-4" />
                </div>
                Sentiment
              </span>
              {metrics.call_analysis?.issues.length ? (
                <span
                  className={cn(
                    "flex items-center gap-3 pt-2 text-xl",
                    sentimentScore < 40 && "text-orange-500",
                  )}
                >
                  {sentimentText}
                  {sentimentBadge}
                </span>
              ) : (
                "-"
              )}
            </div>
          </div>
        </div>
      </div>
      <span className="text-lg font-medium">Insights</span>
      <div
        className={`
          bg-card text-card-foreground overflow-x-auto rounded-md border
          border-gray-300 p-6 shadow-xs
        `}
      >
        <div
          className={`
            grid grid-cols-1 gap-x-10 gap-y-10
            md:grid-cols-2
          `}
        >
          <div className="p-0">
            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium">Audio Quality</span>

              <MetricValue
                label="background noise"
                value={backgroundNoiseCount ? backgroundNoiseCount + 1 : 1}
                checkpoints={{
                  medium: 4,
                  high: 5,
                  max: 6,
                }}
              />

              <MetricValue
                label="transcription quality"
                value={metrics?.transcription_score}
                flipped
                checkpoints={{
                  medium: 0.5,
                  high: 0.2,
                  max: 1,
                }}
              />
            </div>
          </div>

          <div className="p-0">
            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium">Engagement</span>

              <MetricValue
                label="sentiment"
                value={sentimentScore}
                flipped
                checkpoints={{
                  medium: 40,
                  high: 20,
                  max: 100,
                }}
              />

              <MetricValue
                label="user engagement"
                value={getUserEngagement(metrics)}
                flipped
                tooltip="User engagement is the difference in time between when the user is speaking compared to the time the agent is speaking."
                checkpoints={{
                  medium: 20,
                  high: 10,
                  max: 100,
                }}
              />
            </div>
          </div>

          <div className="p-0">
            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium">Agent Performance</span>

              <MetricValue
                label="average call latency"
                value={
                  metrics?.response_time_score
                    ? 1 - metrics.response_time_score
                    : undefined
                }
                checkpoints={{
                  medium: 0.6,
                  high: 0.4,
                  max: 1,
                }}
              />

              <MetricValue
                label="repetition"
                value={repetitionCount || 0}
                showValueInLabel
                checkpoints={{
                  medium: 2,
                  high: 3,
                  max: 5,
                }}
              />
            </div>
          </div>

          <div className="p-0">
            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium">Conversation Flow</span>

              <MetricValue
                label="interruption"
                value={userInterruptionCount + agentInterruptionCount}
                showValueInLabel
                checkpoints={{
                  medium: 3,
                  high: 5,
                  max: 6,
                }}
              />
              <MetricValue
                label="speaker change"
                hoverLabel={`Unique Speakers: ${metrics?.utterance_summary?.uniqueSpeakers?.filter(Boolean).join(", ")}`}
                value={speakerChanges}
                showValueInLabel
                checkpoints={{
                  medium: 3,
                  high: 4,
                  max: 5,
                }}
              />
            </div>
          </div>
        </div>
        <div className="pt-10">
          <QuickStats
            metrics={metrics}
            userInterruptionCount={userInterruptionCount}
            agentInterruptionCount={agentInterruptionCount}
          />
        </div>
      </div>

      {pieChartData.length > 0 && (
        <div
          className={`
            bg-card text-card-foreground rounded-md border border-gray-300 p-6
            shadow-xs
          `}
        >
          <div className="space-y-3">
            <h3
              className={`
                flex items-center gap-2 text-sm font-semibold text-slate-900
              `}
            >
              Utterance Types
            </h3>
            <div className="h-40">
              <PieChart
                removeContainer
                activeTag={undefined}
                data={pieChartData}
                type="utterances"
                className="h-fit"
                isFetching={isLoading}
              />
            </div>
          </div>
        </div>
      )}

      {metrics.call_analysis?.issues &&
        metrics.call_analysis.issues.length > 0 && (
          <>
            <span className="text-lg font-medium">Issues</span>
            <div className="flex flex-col gap-3" id="issues-section">
              <IssuesList issues={metrics?.call_analysis?.issues || []} />
            </div>
          </>
        )}
    </div>
  );
}

function getUserEngagement(metrics: CallMetricsResponse) {
  const userWords = metrics.utterance_summary?.userWords || 0;
  const agentWords = metrics.utterance_summary?.agentWords || 1; // avoid division by zero
  const ratio = userWords / agentWords;
  const userEngagement = Math.min(1, ratio) * 100;
  return userEngagement;
}

function QuickStats({
  metrics,
  userInterruptionCount,
  agentInterruptionCount,
}: {
  metrics: CallMetricsResponse;
  userInterruptionCount: number;
  agentInterruptionCount: number;
}) {
  return (
    <div className="grid w-full grid-cols-2 gap-4 text-sm">
      {metrics.call_analysis?.languages &&
        metrics.call_analysis.languages.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold">Language</span>
            <p>{metrics.call_analysis.languages.join(", ")}</p>
          </div>
        )}
      {metrics?.call_analysis?.customer_demographics && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold">Caller Profile</span>
          <p className="capitalize">
            {metrics?.call_analysis?.customer_demographics?.age_range.replace(
              "_",
              " ",
            )}
            , {metrics?.call_analysis?.customer_demographics?.gender}
          </p>
        </div>
      )}
      {userInterruptionCount + agentInterruptionCount > 0 && (
        <>
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold">
              Total User Interruptions
            </span>
            <p>{userInterruptionCount}</p>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold">
              Total Agent Interruptions
            </span>
            <p>{agentInterruptionCount}</p>
          </div>
        </>
      )}
    </div>
  );
}

function getSentimentText(sentimentScore: number) {
  let text = "Excellent";
  let className = "bg-green-50 text-green-700 ring-green-600/20";
  if (sentimentScore < 36) {
    text = "Poor";
    className = "text-orange-700 bg-orange-50 ring-orange-600/10";
  } else if (sentimentScore < 60) {
    text = "Neutral";
    className = "text-gray-600 bg-gray-50 ring-gray-500/10";
  } else if (sentimentScore < 80) {
    text = "Good";
  }

  const Badge = (
    <span
      className={cn(
        `
          inline-flex items-center rounded-md px-2 py-1 text-xs font-medium
          ring-1 ring-inset
        `,
        className,
      )}
    >
      {sentimentScore}%
    </span>
  );

  return {
    sentimentText: text,
    sentimentBadge: Badge,
  };
}
